/**
 * LanguageContext - i18n state and translations
 *
 * - Persists language in localStorage (key: cai_language)
 * - Syncs with user profile preferences when logged in (getProfile on auth, saveProfile on setLanguage)
 * - Exposes t(key, params?), language, setLanguage
 * - Fallback: missing key → pt-BR, invalid localStorage value → pt-BR
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getProfile, saveProfile } from '@/services/profileService';
import type { Language } from '@/types';

import ptBR from '@/i18n/pt-BR.json';
import en from '@/i18n/en.json';
import es from '@/i18n/es.json';

const TRANSLATIONS: Record<Language, Record<string, unknown>> = {
  'pt-BR': ptBR as Record<string, unknown>,
  en: en as Record<string, unknown>,
  es: es as Record<string, unknown>,
};

const STORAGE_KEY = 'cai_language';
const VALID_LANGUAGES: Language[] = ['pt-BR', 'en', 'es'];

function getStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_LANGUAGES.includes(stored as Language)) {
      return stored as Language;
    }
  } catch {
    // ignore
  }
  return 'pt-BR';
}

function getNested(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function interpolate(str: string, params?: Record<string, string | number>): string {
  if (!params) return str;
  let result = str;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value));
  }
  return result;
}

export type TFunction = (key: string, params?: Record<string, string | number>) => string;

interface LanguageContextType {
  t: TFunction;
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const FALLBACK_CONTEXT: LanguageContextType = {
  t: (key: string) => key,
  language: 'pt-BR',
  setLanguage: () => {},
};

export function useTranslation(): LanguageContextType {
  const ctx = useContext(LanguageContext);
  if (ctx === undefined) {
    return FALLBACK_CONTEXT;
  }
  return ctx;
}

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const [language, setLanguageState] = useState<Language>(getStoredLanguage);

  const setLanguage = useCallback((lang: Language) => {
    if (!VALID_LANGUAGES.includes(lang)) return;
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, []);

  // Sync language from profile when user is available and auth is validated
  useEffect(() => {
    if (loading || !user?.id) return;
    let cancelled = false;
    getProfile(user.id).then((profile) => {
      if (cancelled || !profile?.preferences) return;
      const prefLang = (profile.preferences as { language?: Language }).language;
      if (prefLang && VALID_LANGUAGES.includes(prefLang)) {
        setLanguageState(prefLang);
        try {
          localStorage.setItem(STORAGE_KEY, prefLang);
        } catch {
          // ignore
        }
      }
    });
    return () => { cancelled = true; };
  }, [user?.id, loading]);

  // Persist language to profile when user changes language (and is logged in)
  const setLanguageAndPersist = useCallback(async (lang: Language) => {
    if (!VALID_LANGUAGES.includes(lang)) return;
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    if (user?.id) {
      try {
        const profile = await getProfile(user.id);
        if (profile) {
          const prefs = (profile.preferences || {}) as Record<string, unknown>;
          await saveProfile(user.id, profile, { ...prefs, language: lang });
        }
      } catch (err) {
        // Failed to persist language to profile - non-blocking
      }
    }
  }, [user?.id]);

  const t: TFunction = useCallback((key: string, params?: Record<string, string | number>) => {
    const dict = TRANSLATIONS[language];
    let value: unknown = getNested(dict, key);
    // Simple plural: if params has count and key_other exists, use it when count !== 1
    if (params?.count !== undefined && typeof params.count === 'number' && params.count !== 1) {
      const otherKey = `${key}_other`;
      const otherValue = getNested(dict, otherKey);
      if (typeof otherValue === 'string') value = otherValue;
    }
    if (value === undefined) {
      value = getNested(TRANSLATIONS['pt-BR'], key);
    }
    if (typeof value !== 'string') {
      if (import.meta.env.DEV) console.warn(`[i18n] Missing key: ${key}`);
      return key;
    }
    return interpolate(value, params);
  }, [language]);

  const api: LanguageContextType = {
    t,
    language,
    setLanguage: setLanguageAndPersist,
  };

  return (
    <LanguageContext.Provider value={api}>
      {children}
    </LanguageContext.Provider>
  );
};
